import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { FileText, UserPlus, FilePlus2, ExternalLink, Loader2, CheckCircle2, FileCheck2, FileSignature } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { BackendFunctions } from "@/api/integrations";
import { Presupuesto } from "@/api/entities";
import { Badge } from "@/components/ui/badge";

// Sub-componente para cada tipo de documento (estimate / proforma / invoice).
// Vive en este archivo porque solo lo usa este panel.
function DocRow({ label, state, docType, loading, disabled, disabledReason, onGenerate }) {
  const Icon = docType === 'estimate' ? FileText : docType === 'proforma' ? FileSignature : FileCheck2;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 text-indigo-500 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
          {state.has ? (
            <a href={state.link} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-2 mt-1">
              <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-200">
                {state.number || 'creado'}
              </Badge>
              <span className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center">
                Ver en Facturadirecta <ExternalLink className="w-3 h-3 ml-1" />
              </span>
            </a>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">No generado</p>
          )}
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onGenerate}
        disabled={disabled}
        title={disabled ? disabledReason : (state.has ? 'Regenerar — el anterior queda en FD' : '')}
        className="border-indigo-600 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950"
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {!loading && <FilePlus2 className="w-4 h-4 mr-2" />}
        {state.has ? 'Regenerar' : 'Generar'}
      </Button>
    </div>
  );
}

export default function FacturaDirectaPanel({ lead, onUpdate }) {
  const { toast } = useToast();
  const [loadingContact, setLoadingContact] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const hasContact = !!(lead.factura_directa_id || lead.factura_directa_sandbox_id);
  const contactLink = lead.factura_directa_link || lead.factura_directa_sandbox_link;

  // #19/3 — 3 tipos de documento. Cada uno tiene sus campos en el lead.
  const docState = (kind) => {
    const id = lead[`factura_directa_${kind}_id`] || lead[`factura_directa_sandbox_${kind}_id`];
    const number = lead[`factura_directa_${kind}_number`] || lead[`factura_directa_sandbox_${kind}_number`];
    const link = lead[`factura_directa_${kind}_link`] || lead[`factura_directa_sandbox_${kind}_link`];
    return { id, number, link, has: !!id };
  };
  const invoiceState = docState('invoice');
  const proformaState = docState('proforma');
  const estimateState = docState('estimate');

  // #19/2 — para habilitar el botón Generar Factura: necesitamos o un
  // presupuesto guardado para este lead o lead.importe > 0.
  const { data: presupuestosLead = [] } = useQuery({
    queryKey: ['presupuestos', lead.id],
    queryFn: () => Presupuesto.list({ lead_id: lead.id }, { orderBy: 'created_date', orderDirection: 'desc' }),
    enabled: !!lead.id,
  });
  const hasPresupuesto = presupuestosLead.length > 0 &&
    Array.isArray(presupuestosLead[0].lineas) &&
    presupuestosLead[0].lineas.some((l) => Number(l.cantidad) > 0 || Number(l.precio_unitario) > 0 || Number(l.precio_venta) > 0);
  const canInvoice = hasPresupuesto || (Number(lead.importe) > 0);

  // Formulario del modal — pre-relleno con lo que ya tiene el lead.
  const [form, setForm] = useState({
    nombre: "", apellidos: "", email: "", telefono: "", empresa: "",
    nif: "", direccion: "", codigo_postal: "", poblacion: "",
  });
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Re-sincroniza el form cuando se abre el modal o cambia el lead.
  useEffect(() => {
    if (!modalOpen) return;
    setForm({
      nombre: lead.nombre || "",
      apellidos: lead.apellidos || "",
      email: lead.email || "",
      telefono: lead.telefono || "",
      empresa: lead.empresa || "",
      nif: lead.nif || "",
      direccion: lead.direccion || "",
      codigo_postal: lead.codigo_postal || "",
      poblacion: lead.poblacion || "",
    });
  }, [modalOpen, lead.id, lead.nombre, lead.apellidos, lead.email, lead.telefono, lead.empresa, lead.nif, lead.direccion, lead.codigo_postal, lead.poblacion]);

  const handleSyncContact = async () => {
    if (!form.nombre.trim()) {
      toast({ title: "Falta el nombre", variant: "destructive" });
      return;
    }
    if (!form.email.trim()) {
      toast({ title: "Falta el email", variant: "destructive" });
      return;
    }
    setLoadingContact(true);
    try {
      // 1) Persistir los campos fiscales en el lead (Firestore).
      await onUpdate({
        id: lead.id,
        data: {
          nombre: form.nombre.trim(),
          apellidos: form.apellidos.trim(),
          email: form.email.trim(),
          telefono: form.telefono.trim(),
          empresa: form.empresa.trim(),
          nif: form.nif.trim(),
          direccion: form.direccion.trim(),
          codigo_postal: form.codigo_postal.trim(),
          poblacion: form.poblacion.trim(),
        },
      });

      // 2) Crear/actualizar contacto en Facturadirecta. El backend lee la
      //    versión fresca del lead, así que recoge los campos guardados arriba.
      const res = await BackendFunctions.createFacturaDirectaContact({ leadId: lead.id });

      // 3) Persistir las referencias devueltas por FD (id + link) en el lead.
      await onUpdate({
        id: lead.id,
        data: {
          [res.isSandbox ? 'factura_directa_sandbox_id' : 'factura_directa_id']: res.contactId,
          [res.isSandbox ? 'factura_directa_sandbox_link' : 'factura_directa_link']: res.link,
        },
      });

      toast({
        title: hasContact ? "Cliente actualizado" : "Cliente sincronizado",
        description: "Datos guardados en el lead y propagados a Facturadirecta.",
      });
      setModalOpen(false);
    } catch (error) {
      toast({
        title: "Error sincronizando cliente",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingContact(false);
    }
  };

  // #19/3 — 1 handler para los 3 tipos de documento. El backend devuelve los
  // nombres exactos de los campos a guardar (idField/numberField/linkField),
  // así no hay que reconstruir aquí el prefix sandbox/prod.
  const [loadingDocType, setLoadingDocType] = useState(null);
  const labelByDocType = { invoice: 'Factura', proforma: 'Proforma', estimate: 'Presupuesto FD' };
  const generateDoc = async (docType) => {
    try {
      setLoadingDocType(docType);
      const res = await BackendFunctions.createFacturaDirectaInvoice({ leadId: lead.id, docType });
      const update = { updated_date: new Date().toISOString() };
      if (res.idField) update[res.idField] = res.invoiceId;
      if (res.numberField) update[res.numberField] = res.invoiceNumber;
      if (res.linkField) update[res.linkField] = res.link;
      await onUpdate({ id: lead.id, data: update });
      toast({
        title: `${labelByDocType[docType]} generad${docType === 'invoice' || docType === 'proforma' ? 'a' : 'o'}`,
        description: `${res.invoiceNumber} creado exitosamente en Facturadirecta.`,
      });
    } catch (error) {
      toast({
        title: `Error generando ${labelByDocType[docType]}`,
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoadingDocType(null);
    }
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800 mt-6">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-gray-900 dark:text-white">
          <FileText className="w-5 h-5 text-indigo-500" />
          Factura Directa
          {hasContact && (
            <Badge className="ml-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800/60 text-[10px] flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Contacto sincronizado
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Contact Status */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Cliente en Factura Directa
            </p>
            {hasContact ? (
              <a
                href={contactLink}
                target="_blank"
                rel="noreferrer"
                className="text-xs flex items-center gap-2 mt-1"
              >
                <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-200">
                  Sincronizado
                </Badge>
                <span className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center">
                  Ver en Facturadirecta <ExternalLink className="w-3 h-3 ml-1" />
                </span>
              </a>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                No sincronizado
              </p>
            )}
          </div>

          <Button
            variant={hasContact ? "outline" : "default"}
            size="sm"
            onClick={() => setModalOpen(true)}
            disabled={loadingContact}
            className={hasContact ? "text-gray-600" : "bg-indigo-600 hover:bg-indigo-700 text-white"}
          >
            {loadingContact && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {!loadingContact && <UserPlus className="w-4 h-4 mr-2" />}
            {hasContact ? "Actualizar Cliente" : "Sincronizar Cliente"}
          </Button>
        </div>

        {/* #19/3 — 3 tipos de documento: Presupuesto FD, Proforma, Factura.
            Reglas de habilitación:
              - Presupuesto FD: requiere canInvoice (no hace falta contacto en FD).
              - Proforma:      requiere contacto sincronizado + canInvoice.
              - Factura:       requiere contacto sincronizado + canInvoice. */}
        <DocRow
          label="Presupuesto Facturadirecta"
          state={estimateState}
          docType="estimate"
          loading={loadingDocType === 'estimate'}
          disabled={!canInvoice || loadingDocType !== null}
          disabledReason={!canInvoice ? 'Necesitas un presupuesto guardado o lead.importe > 0' : ''}
          onGenerate={() => generateDoc('estimate')}
        />
        <DocRow
          label="Proforma"
          state={proformaState}
          docType="proforma"
          loading={loadingDocType === 'proforma'}
          disabled={!hasContact || !canInvoice || loadingDocType !== null}
          disabledReason={!hasContact ? 'Sincroniza primero el contacto' : (!canInvoice ? 'Necesitas un presupuesto guardado o lead.importe > 0' : '')}
          onGenerate={() => generateDoc('proforma')}
        />
        <DocRow
          label="Factura definitiva"
          state={invoiceState}
          docType="invoice"
          loading={loadingDocType === 'invoice'}
          disabled={!hasContact || !canInvoice || loadingDocType !== null}
          disabledReason={!hasContact ? 'Sincroniza primero el contacto' : (!canInvoice ? 'Necesitas un presupuesto guardado o lead.importe > 0' : '')}
          onGenerate={() => generateDoc('invoice')}
        />

      </CardContent>

      {/* Modal de datos fiscales */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-indigo-600" />
              {hasContact ? "Actualizar cliente en Facturadirecta" : "Sincronizar cliente con Facturadirecta"}
            </DialogTitle>
            <DialogDescription>
              Confirma o completa los datos fiscales del cliente. Se guardan en el lead del CRM y se propagan a Facturadirecta.
              Sin NIF/CIF la factura saldrá como simplificada (válida hasta 400€).
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input value={form.nombre} onChange={setField('nombre')} className="dark:bg-gray-800" />
            </div>
            <div>
              <Label className="text-xs">Apellidos</Label>
              <Input value={form.apellidos} onChange={setField('apellidos')} className="dark:bg-gray-800" />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={setField('email')} className="dark:bg-gray-800" />
            </div>
            <div>
              <Label className="text-xs">Teléfono</Label>
              <Input value={form.telefono} onChange={setField('telefono')} className="dark:bg-gray-800" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Empresa</Label>
              <Input value={form.empresa} onChange={setField('empresa')} className="dark:bg-gray-800" />
            </div>
            <div>
              <Label className="text-xs">NIF / CIF</Label>
              <Input value={form.nif} onChange={setField('nif')} placeholder="opcional pero recomendado" className="dark:bg-gray-800" />
            </div>
            <div>
              <Label className="text-xs">Código postal</Label>
              <Input value={form.codigo_postal} onChange={setField('codigo_postal')} className="dark:bg-gray-800" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Dirección</Label>
              <Input value={form.direccion} onChange={setField('direccion')} className="dark:bg-gray-800" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Población</Label>
              <Input value={form.poblacion} onChange={setField('poblacion')} className="dark:bg-gray-800" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={loadingContact}>
              Cancelar
            </Button>
            <Button
              onClick={handleSyncContact}
              disabled={loadingContact}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {loadingContact && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {hasContact ? "Actualizar en Facturadirecta" : "Sincronizar con Facturadirecta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
